import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Link, LogSentryException, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { Controller, useForm } from "react-hook-form";

import useNavigation from "../../hooks/navigation";
import useUrlHelper from "../../hooks/urlHelper";
import { useSimulateTabFocusInApp } from "../../hooks/useSimulateTabFocusInApp";
import useTranslation from "../../hooks/useTranslation";
import { MAX_NAME_LENGTH, userNameSchema } from "../../hooks/useUpdateName";
import { AuthError } from "../../types";
import { isEmailInvalid } from "../../utils/helpers";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";
import { ValidatedPasswordInput } from "../ValidatedPasswordInput/ValidatedPasswordInput";
import Disclaimer from "./Disclaimer/Disclaimer";
import { type SignUpFormData, useSignupSchema } from "./SignUpSchema";

import style from "./SignUp.module.css";

interface SignUpProps {
  submit: (email: string, password: string, name: string, recaptcha?: string) => void;
  inProgress: boolean;
  signupError?: AuthError | null;
  disableCaptcha?: boolean; // only for testing
}

const SignUp = (props: SignUpProps) => {
  useSimulateTabFocusInApp();
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);

  const { t, m } = useTranslation();
  const errorMessage = {
    InvalidEmail: t(m.ErrorMessage.InvalidEmail),
    EmptyPassword: t(m.ErrorMessage.InvalidPassword),
    EmptyName: t(m.ErrorMessage.EmptyName),
    WeakPassword: t(m.ErrorMessage.WeakPassword),
    TooLongPassword: t(m.ErrorMessage.TooLongPassword),
    PasswordsDontMatch: t(m.ErrorMessage.PasswordsDontMatch),
    TermsAndPrivacyNotAccepted: t(m.ErrorMessage.TermsAndPrivacyNotAccepted),
    EmailAlreadyUsed: t(m.ErrorMessage.EmailAlreadyUsed),
    CaptchaError: t(m.ErrorMessage.CaptchaError),
    Unknown: t(m.ErrorMessage.Unknown),
  };
  const signupSchema = useSignupSchema();

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    trigger,
    formState: { errors },
    register,
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      passwordConfirm: "",
      name: "",
      terms: false,
    },
    mode: "onSubmit",
  });

  const email = watch("email");
  const userName = watch("name");
  const password = watch("password");
  const passwordConfirm = watch("passwordConfirm");

  const debouncedEmailValidation = useMemo(
    () =>
      debounce((email: string) => {
        if (email && isEmailInvalid(email)) {
          setError("email", { message: errorMessage.InvalidEmail });
        } else if (email) {
          clearErrors("email");
        }
      }, 600),
    [errorMessage.InvalidEmail, setError, clearErrors]
  );

  const debouncedPasswordValidation = useMemo(
    () =>
      debounce((pass: string, confirm: string) => {
        if (confirm) {
          if (pass !== confirm) {
            setError("passwordConfirm", { message: errorMessage.PasswordsDontMatch });
          } else {
            clearErrors("passwordConfirm");
          }
        }
      }, 600),
    [setError, clearErrors, errorMessage.PasswordsDontMatch]
  );

  const debouncedNameValidation = useMemo(
    () =>
      debounce((name: string) => {
        if (name && !userNameSchema.safeParse(name).success) {
          trigger("name");
        } else {
          clearErrors("name");
        }
      }, 600),
    [clearErrors, trigger]
  );

  useEffect(() => {
    if (email) {
      debouncedEmailValidation(email);
    } else {
      debouncedEmailValidation.cancel();
      clearErrors("email");
    }
  }, [email, debouncedEmailValidation, clearErrors]);

  useEffect(() => {
    if (password && passwordConfirm) {
      debouncedPasswordValidation(password, passwordConfirm);
    } else if (password && !passwordConfirm) {
      debouncedPasswordValidation.cancel();
      clearErrors("passwordConfirm");
    }
  }, [password, passwordConfirm, debouncedPasswordValidation, clearErrors]);

  useEffect(() => {
    if (userName) {
      debouncedNameValidation(userName);
    } else {
      debouncedNameValidation.cancel();
      clearErrors("name");
    }
  }, [clearErrors, debouncedNameValidation, userName]);

  useEffect(() => {
    switch (props.signupError) {
      case AuthError.InvalidEmail:
        setError("email", { message: errorMessage.InvalidEmail });
        break;
      case AuthError.EmailAlreadyUsed:
        setError("email", { message: errorMessage.EmailAlreadyUsed });
        break;
      case AuthError.WeakPassword:
        setError("password", { message: errorMessage.WeakPassword });
        break;
      case AuthError.TooLongPassword:
        setError("password", { message: errorMessage.TooLongPassword });
        break;
      case AuthError.Unknown:
        setError("root", { message: errorMessage.Unknown });
        break;
      case AuthError.MissingCaptcha:
        setIsCaptchaEnabled(true);
        break;
    }
  }, [
    props.signupError,
    errorMessage.EmailAlreadyUsed,
    errorMessage.InvalidEmail,
    errorMessage.Unknown,
    setError,
    errorMessage.WeakPassword,
    errorMessage.TooLongPassword,
  ]);

  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);

  const onSubmitForm = useCallback(
    async (data: SignUpFormData) => {
      if (props.disableCaptcha || !isCaptchaEnabled) {
        props.submit(data.email, data.password, data.name);
        return;
      }
      if (recaptchaRef.current === null) {
        setError("root", { message: errorMessage.CaptchaError });
        return;
      }

      if (isRecaptchaLoaded) {
        try {
          const recaptchaValue = await recaptchaRef.current.execute();
          if (recaptchaValue === "cancel") {
            return;
          }

          if (!recaptchaValue) {
            setError("root", { message: errorMessage.CaptchaError });
            return;
          }

          props.submit(data.email, data.password, data.name, recaptchaValue);
        } catch (error) {
          setError("root", { message: errorMessage.CaptchaError });
          LogSentryException({ messagePrefix: "Captcha error", error });
        }
      }
    },
    [props, isCaptchaEnabled, isRecaptchaLoaded, errorMessage.CaptchaError, setError]
  );

  const navigation = useNavigation();
  const handleSignIn = () => {
    navigation.goToSignIn();
  };

  const urlHelper = useUrlHelper();
  let title = urlHelper.isFormEmbedded()
    ? t(m.SignUp.FirstCreateAnAccount)
    : t(m.SignUp.CreateAnAccount);

  if (urlHelper.getQueryParams().voucherId) {
    title = "Create an account to claim your voucher";
  }

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      isRecaptchaLoaded &&
      recaptchaRef.current !== null &&
      props.signupError === AuthError.MissingCaptcha
    ) {
      handleSubmit(onSubmitForm)();
    }
  }, [
    isCaptchaEnabled,
    recaptchaRef,
    handleSubmit,
    onSubmitForm,
    isRecaptchaLoaded,
    props.signupError,
  ]);

  return (
    <FormWrapper>
      <div className={style.modalContainer}>
        <Heading type="h1" aria-label="title">
          {title}
        </Heading>
        <div className={style.signIn}>
          {t(m.SignUp.AlreadyHaveAnAccountQuestion)}&nbsp;
          <Link onClick={handleSignIn}>{t(m.General.Login)}</Link>
        </div>
        <form className={style.signUpForm} onSubmit={handleSubmit(onSubmitForm)} noValidate>
          <TextField
            tabIndex={0}
            errorMessage={errors.email?.message}
            type="email"
            aria-label="email"
            placeholder={t(m.General.WorkEmail)}
            autoComplete="email"
            error={Boolean(errors.email)}
            disabled={props.inProgress}
            autoFocus
            {...register("email")}
          />
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => (
              <ValidatedPasswordInput
                tabIndex={0}
                placeholder={t(m.General.Password)}
                value={field.value}
                onChange={field.onChange}
                autoComplete="new-password"
                error={Boolean(fieldState.error)}
                errorMessage={fieldState.error?.message}
                disabled={props.inProgress}
                passwordConfirm={passwordConfirm}
              />
            )}
          />
          <TextField
            tabIndex={0}
            errorMessage={errors.passwordConfirm?.message}
            type="password"
            aria-label="repeat password"
            placeholder={t(m.General.ReEnterPassword)}
            autoComplete="new-password"
            error={Boolean(errors.passwordConfirm)}
            disabled={props.inProgress}
            {...register("passwordConfirm")}
          />
          <TextField
            tabIndex={0}
            errorMessage={errors.name?.message}
            aria-label="name"
            placeholder={t(m.General.Name)}
            autoComplete="cc-name"
            error={Boolean(errors.name)}
            disabled={props.inProgress}
            maxLength={MAX_NAME_LENGTH}
            {...register("name")}
          />
          <div>
            <Controller
              name="terms"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Disclaimer
                    tabIndex={0}
                    className={classNames(style.disclaimer)}
                    areTermsAccepted={field.value}
                    handleToggleTermsAccepted={() => field.onChange(!field.value)}
                    disabled={props.inProgress}
                  />
                  {fieldState.error && (
                    <span className={style.disclaimerErrorMessage}>{fieldState.error.message}</span>
                  )}
                </>
              )}
            />
          </div>
          {isCaptchaEnabled && !props.disableCaptcha && (
            <ReCAPTCHA
              ref={recaptchaRef}
              onLoad={() => {
                setIsRecaptchaLoaded(true);
              }}
            />
          )}
          {
            <div className={style.disclaimerErrorMessage} role="alert">
              {errors.root?.message}
            </div>
          }
          <Button
            tabIndex={0}
            id="signup-button"
            type="submit"
            aria-label="signup"
            disabled={props.inProgress}
          >
            {t(m.General.SignUp)}
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};

export default SignUp;
